import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useProfileManagement } from "./useProfileManagement";
import { useAuthNavigation } from "./useAuthNavigation";
import type { AuthChangeEvent } from "@supabase/supabase-js";

export const useAuthRedirect = (
  loginType: string,
  setLoginType: (type: "owner" | "admin") => void
) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { createOrUpdateProfile } = useProfileManagement();
  const { handleAuthNavigation } = useAuthNavigation();

  useEffect(() => {
    const handleTokenCheck = () => {
      const token = searchParams.get("token");
      const type = searchParams.get("type");

      if (token && (type === "recovery" || type === "invite")) {
        console.log("Token detected:", type);
        setLoginType("admin");
        toast.info("Veuillez définir votre nouveau mot de passe pour continuer");
      }
    };

    const handleAuthStateChange = async (
      event: AuthChangeEvent,
      session: any
    ) => {
      console.log("Auth state changed:", event);

      if (event === "SIGNED_IN" && session?.user?.id) {
        try {
          const profile = await createOrUpdateProfile(
            session.user.id,
            loginType as "owner" | "admin"
          );

          if (profile) {
            handleAuthNavigation(profile.login_type, profile.user_type);
          } else {
            // If no profile, try to navigate based on loginType
            handleAuthNavigation(loginType);
          }
        } catch (error) {
          console.error("Error in auth state change:", error);
          toast.error("Erreur lors de la connexion");
        }
      }

      switch (event) {
        case "SIGNED_OUT":
          toast.info("Vous avez été déconnecté");
          // Rediriger vers la page de connexion propriétaire si c'était un propriétaire
          if (loginType === "owner") {
            navigate("/login-owner");
          } else {
            navigate("/login-admin");
          }
          break;
        case "USER_UPDATED":
          toast.success("Profil mis à jour avec succès");
          break;
        case "PASSWORD_RECOVERY":
          toast.info("Vérifiez vos emails pour réinitialiser votre mot de passe");
          break;
      }
    };

    const initializeAuth = async () => {
      handleTokenCheck();
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.id) {
        const profile = await createOrUpdateProfile(
          session.user.id,
          loginType as "owner" | "admin"
        );
        if (profile) {
          handleAuthNavigation(profile.login_type, profile.user_type);
        }
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      handleAuthStateChange
    );

    return () => subscription.unsubscribe();
  }, [navigate, searchParams, setLoginType, loginType]);
};